import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import moment from "moment";
import { deSelectTeeTime } from "redux/actions/teesheet";
import { showError, showSuccess, closeNewLotteryRequest, enqueue, dequeue } from "redux/actions/ui";
import { ICustomer } from "redux/reducers/models/customer";
import { ISlot, ITeeTime, ITeeTimeNew, ITeeTimeSlotNew } from "redux/reducers/models/teetime";
import { StatusCode } from "api/protocols";
import { customerErrorMessage } from "helpers/Helpers";
import { isTurnBlockedForEighteenHoles } from "helpers/teeSheetTurn";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import Radio from "components/radio";
import Popup from "components/popup/Popup";
import NewCustomer, { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import { useTranslation } from "react-i18next";
import "public/scss/teesheet.scss";
import "elements/teesheet/bookingModal.scss";
import Portal from "elements/Portal";
import Spin from "components/spin/spin";
import { GetCustomer, PostCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { TLoadingAddBookingsAction, TLoadingTeetimeTabsActionStatus } from "../loadingTeetimeTabActions";
import { Badge } from "components/badge/Badge";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ILottery } from "redux/reducers/models/lottery";
import FormLayout from "components/form/FormLayout";
import { PostTeeTimeLotteryRequest } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import Toggle from "components/form/toggle/Toggle";
import { PostBooking, PutBookingConfirmation } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import Checkbox from "components/form/checkbox/Checkbox";
import { ILotteryBuild } from "../NewTeeSheetTabs";

export interface ICartsAvailableNew {
  nineHole: number;
  eighteenHole: number;
}

interface IBookingModalNewProps {
  slot: ITeeTimeSlotNew;
  getNextTeeTimeStartTime(currentTeeTime: ITeeTime): string;
  loading: boolean;
  loadingText: string;
  reloadTeeTimes: () => void;
  reloadActionTeeTimeTabs: (addTeetimeTabAction: TLoadingAddBookingsAction) => Promise<TLoadingTeetimeTabsActionStatus>;
  lottery: ILottery;
  lotteryTeeTimes: Array<ITeeTimeNew>;
  cartsAvailable: ICartsAvailableNew;
  lotteryBuilds: Array<ILotteryBuild>;
}

interface IMainState {
  [key: string]: any;
  golferSearchQuery1: string;
  golferSearchQuery2: string;
  golferSearchQuery3: string;
  golferSearchQuery4: string;
  golferSearchQuery5: string;
  golferSearchQuery6: string;
  golferSearchResult1: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult2: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult3: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult4: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult5: (Partial<ICustomer> | ICustomer)[];
  golferSearchResult6: (Partial<ICustomer> | ICustomer)[];
  searching: boolean;
}

export interface INewBookingState {
  [key: string]: any;
  holes: number;
  quantity: number;
  powerCartQuantity: number;
  teeTimeSlots: {
    id: number;
    customer_id: number;
    guest: boolean;
  }[];
  customerSelection: ICustomer[];
  teetimeLockPopupVisible: boolean;

  holeButtons: boolean[]; // disabled
  playerButtons: boolean[]; // disabled

  requestPosting: boolean;

  startTimePreferenceWindow: number;
  timePreference1: string;
  timePreference2: string;
  timePreference3: string;

  forceBooking: boolean;
  sendBookingConfirmation: boolean;
}

export interface INewPlayerState {
  newCustomer: ICustomer;
  selectedSlot: ISlot;
  newCustomerSheetActive: boolean;
  currentSearch: string;
}

export interface IBooking {
  id: number;
  tee_time_id: number;
}

export enum StateType {
  MainState = "IMainState",
  NewBookingState = "INewBookingState",
  NewPlayerState = "INewPlayerState",
}

export default function LotteryRequestModal(props: IBookingModalNewProps) {
  const { Option } = Select;

  const { teeSheetStore, uiStore, facilityStore } = useAppSelector(state => state);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [state, setState] = useState<IMainState>({
    golferSearchQuery1: "",
    golferSearchQuery2: "",
    golferSearchQuery3: "",
    golferSearchQuery4: "",
    golferSearchQuery5: "",
    golferSearchQuery6: "",
    golferSearchResult1: [null],
    golferSearchResult2: [null],
    golferSearchResult3: [null],
    golferSearchResult4: [null],
    golferSearchResult5: [null],
    golferSearchResult6: [null],
    searching: false,
  });

  const [newBookingState, setNewBookingState] = useState<INewBookingState>({
    holes: /*18*/ null,
    quantity: /*2*/ null,
    powerCartQuantity: null,
    teeTimeSlots: [],

    customerSelection: [],
    teetimeLockPopupVisible: false,

    holeButtons: [false, false], // disabled
    playerButtons: [false, false, false, false], // disabled

    requestPosting: false,

    startTimePreferenceWindow: 1,
    timePreference1: "",
    timePreference2: "",
    timePreference3: "",

    forceBooking: false,
    sendBookingConfirmation: true,
  });

  const [newPlayerState, setNewPlayerState] = useState<INewPlayerState>({
    newCustomer: null,
    selectedSlot: null,
    newCustomerSheetActive: false,
    currentSearch: "",
  });

  function updateState<T>(newState: Partial<T>, type: StateType) {
    const states = {
      [StateType.MainState]: (newState: Partial<T>) =>
        setState((cur: IMainState) => {
          return { ...cur, ...newState };
        }),
      [StateType.NewBookingState]: (newState: Partial<T>) =>
        setNewBookingState((cur: INewBookingState) => {
          return { ...cur, ...newState };
        }),
      [StateType.NewPlayerState]: (newState: Partial<T>) =>
        setNewPlayerState((cur: INewPlayerState) => {
          return { ...cur, ...newState };
        }),
    };

    states[type](newState);
  }

  async function createForcedBooking() {
    const teeTimeSlots = [...newBookingState.teeTimeSlots];
    // Handle booking guests
    if (newBookingState.quantity > newBookingState.teeTimeSlots.length) {
      const diff = newBookingState.quantity - newBookingState.teeTimeSlots.length;
      const remainingSlots = teeSheetStore.selectedTeeTime.slots.filter(
        slot =>
          slot.status === "available" &&
          !newBookingState.teeTimeSlots.some(selectedSlot => slot.id === selectedSlot.id),
      );
      for (let i = 0; i < diff; i++) {
        teeTimeSlots.push({
          id: remainingSlots[i].id,
          customer_id: newBookingState.customerSelection[0].id,
          guest: true,
        });
      }
    }
    dispatch(enqueue());
    const bookingRes = await PostBooking(
      {
        tee_time_id: teeSheetStore.selectedTeeTime.id,
        quantity: newBookingState?.quantity,
        holes: newBookingState?.holes,
        power_cart_quantity: newBookingState?.powerCartQuantity,
        customer_id: newBookingState.customerSelection[0].id,
        send_confirmation: false,
        tee_time_slots: teeTimeSlots,
      },
      false,
    );

    if (bookingRes?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(typeof bookingRes?.data === "string" ? bookingRes?.data : t("Error creating booking")));
      return;
    }

    if (newBookingState?.sendBookingConfirmation) {
      const confirmationRes = await PutBookingConfirmation(
        { booking_group_id: bookingRes.data.booking_group_id },
        false,
      );

      if (confirmationRes.status !== StatusCode.OK) {
        dispatch(dequeue());
        dispatch(
          showError(
            typeof confirmationRes?.data === "string" ? confirmationRes?.data : t("Error sending confirmation email"),
          ),
        );
        return;
      }
    }
    dispatch(dequeue());

    // 18 on a 9-hole layout books the linked turn row too
    const lotteryReloadIds = [teeSheetStore.selectedTeeTime.id];
    const lotteryTurnTeeTimeId = teeSheetStore.selectedTeeTime?.turn_tee_time?.id;
    if (
      newBookingState?.holes === 18 &&
      lotteryTurnTeeTimeId != null &&
      !lotteryReloadIds.includes(lotteryTurnTeeTimeId)
    ) {
      lotteryReloadIds.push(lotteryTurnTeeTimeId);
    }

    // force refresh, player appears
    void props.reloadActionTeeTimeTabs({
      type: "add-bookings",
      ids: lotteryReloadIds,
    });

    void closeNewRequest();
  }

  async function handleOnOk() {
    //Force create booking
    if (newBookingState?.forceBooking) {
      await createForcedBooking();
    } else {
      //Submit a lottery request
      await submitRequest();
    }
  }

  async function submitRequest() {
    const teeTimeSlots = newBookingState?.teeTimeSlots;
    const bookingQuantity = newBookingState?.quantity;

    // Handle booking guests
    if (newBookingState.quantity > newBookingState.teeTimeSlots.length) {
      //Display error message if guests are present, but not allowed for this lottery
      if (!props?.lottery?.allow_guests) {
        dispatch(showError(t("Guests are not allowed for this lottery")));
        return;
      }
      const diff = bookingQuantity - newBookingState?.teeTimeSlots?.length;
      const remainingSlots = teeSheetStore?.selectedTeeTime?.slots?.filter(
        slot =>
          slot.status === "available" &&
          !newBookingState?.teeTimeSlots?.some(selectedSlot => slot?.id === selectedSlot?.id),
      );
      for (let i = 0; i < diff; i++) {
        teeTimeSlots.push({
          id: remainingSlots[i]?.id,
          customer_id: newBookingState?.customerSelection[0]?.id,
          guest: true,
        });
      }
      updateState<INewBookingState>({ teeTimeSlots }, StateType.NewBookingState);
    }

    const numberOfTimePreferences = props?.lottery?.number_of_time_preferences;
    const allowTimePreferenceWindow = props?.lottery?.allow_time_preference_window;

    const requestParams = {
      lottery_id: props?.lottery?.id,
      customer_id: teeTimeSlots[0]?.customer_id,
      //   group_id: "1234",
      quantity: newBookingState?.quantity,
      holes: newBookingState?.holes,
      start_time_preference_one: newBookingState?.timePreference1,
      start_time_preference_two: numberOfTimePreferences > 1 ? newBookingState?.timePreference2 : null,
      start_time_preference_three: numberOfTimePreferences > 2 ? newBookingState?.timePreference3 : null,
      start_time_preference_window: allowTimePreferenceWindow ? newBookingState?.startTimePreferenceWindow : null,
      customer_id_one: teeTimeSlots[0]?.customer_id,
      customer_id_two: teeTimeSlots[1] ? teeTimeSlots[1]?.customer_id : null,
      customer_id_three: teeTimeSlots[2] ? teeTimeSlots[2]?.customer_id : null,
      customer_id_four: teeTimeSlots[3] ? teeTimeSlots[3]?.customer_id : null,
    };

    const res = await PostTeeTimeLotteryRequest(requestParams, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error creating lottery request")));
      return;
    }

    dispatch(showSuccess(t("Successfully created lottery request")));

    void closeNewRequest();
  }

  function closeNewRequest() {
    dispatch(closeNewLotteryRequest());
    updateState<INewBookingState>(
      {
        customerSelection: [],

        quantity: null,
        powerCartQuantity: 0,
        holes: null,

        teeTimeSlots: [],
        holeButtons: [false, false], // disabled
        playerButtons: [false, false, false, false], // disabled
        teetimeLockPopupVisible: false,
        requestPosting: false,
        startTimePreferenceWindow: 1,
        timePreference1: "",
        timePreference2: "",
        timePreference3: "",
        forceBooking: false,
        sendBookingConfirmation: true,
      },
      StateType.NewBookingState,
    );

    // clear search and result
    updateState<IMainState>(
      {
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
        golferSearchQuery5: "",
        golferSearchQuery6: "",
        golferSearchResult1: [null],
        golferSearchResult2: [null],
        golferSearchResult3: [null],
        golferSearchResult4: [null],
        golferSearchResult5: [null],
        golferSearchResult6: [null],
        searching: false,
      },
      StateType.MainState,
    );

    dispatch(deSelectTeeTime());
  }

  const handleCustomerSearch = (query: string, name: string) => {
    updateState<IMainState>({ [name]: query }, StateType.MainState);
    setNewPlayerState(prevState => ({
      ...prevState,
      currentSearch: query,
    }));
  };

  async function loadCustomers(golferSearchQuery: string, cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: golferSearchQuery }, false, cancelToken);

    if (customerRes.status !== StatusCode.OK) {
      return [];
    }

    return customerRes.data as Array<Record<string, any>>;
  }

  const search = async (
    mounted: boolean,
    golferSearchQuery: string,
    golferSearchResult: string,
    cancelToken: CancelToken,
  ) => {
    try {
      if (golferSearchQuery === "") {
        if (mounted) {
          updateState<IMainState>({ [golferSearchResult]: [null] }, StateType.MainState);
        }
        return;
      } else {
        updateState<IMainState>({ searching: true }, StateType.MainState);
        const res = await loadCustomers(golferSearchQuery, cancelToken);
        if (mounted) {
          updateState<IMainState>({ [golferSearchResult]: res, searching: false }, StateType.MainState);
        }
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  };

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery1, "golferSearchResult1", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult1: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery1]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery2, "golferSearchResult2", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult2: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery2]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery3, "golferSearchResult3", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult3: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery3]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery4, "golferSearchResult4", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult4: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery4]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery5, "golferSearchResult5", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult5: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery5]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    if (mounted === true) {
      timeoutId = window.setTimeout(() => {
        void search(mounted, state.golferSearchQuery6, "golferSearchResult6", source.token);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      updateState<IMainState>({ golferSearchResult6: [null] }, StateType.MainState);
      source.cancel("Cancelled");
    };
  }, [state.golferSearchQuery6]);

  /**
   * compares tee_time_quantity and turn_tee_time quantity and returns the lower quantity
   * if turn_tee_time_quantity is null turn_tee_time will be returned
   * @param tee_time_quantity
   * @param turn_tee_time_quantity
   * @returns number
   */
  const lowerQuantity = (tee_time_quantity: number, turn_tee_time_quantity: number, selected_holes: number): number => {
    if (turn_tee_time_quantity === null) {
      return tee_time_quantity;
    } else if (selected_holes === 9) {
      return tee_time_quantity;
    } else {
      return Math.min(tee_time_quantity, turn_tee_time_quantity);
    }
  };

  useEffect(() => {
    if (!teeSheetStore.selectedTeeTime) {
      return;
    }

    // disabling options...
    let mounted = true;

    const disableOptions = () => {
      const holeButtons: boolean[] = [true, true];
      let playerButtons: boolean[] = [false, false, false, false];

      let totalBuildQuantity = 0;
      if (
        props?.lotteryBuilds?.some(build => build.tee_time_id === teeSheetStore?.selectedTeeTime?.id) &&
        newBookingState?.forceBooking
      ) {
        totalBuildQuantity = props?.lotteryBuilds
          ?.filter(build => build?.tee_time_id === teeSheetStore?.selectedTeeTime?.id)
          ?.reduce((total, build) => {
            return total + build?.quantity;
          }, 0);
      }

      const quantity_remaining = lowerQuantity(
        teeSheetStore.selectedTeeTime?.quantity_remaining - (totalBuildQuantity ?? 0),
        teeSheetStore.selectedTeeTime?.turn_tee_time?.quantity_remaining - (totalBuildQuantity ?? 0),
        newBookingState.holes,
      );

      // disabling number of players based on quantity remaining
      switch (quantity_remaining) {
        case 1:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i === 0) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 2:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i === 0 || i === 1) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 3:
          playerButtons = playerButtons.map((disabled, i) => {
            if (i !== 3) {
              return (disabled = false);
            } else {
              return (disabled = true);
            }
          });
          break;

        case 4:
          playerButtons = playerButtons.map(disabled => {
            disabled = false;
            return disabled;
          });
          break;

        case 5:
          playerButtons = playerButtons.map(disabled => {
            disabled = false;
            return disabled;
          });
          break;

        case 6:
          playerButtons = playerButtons.map(disabled => {
            disabled = false;
            return disabled;
          });
          break;
      }

      const bookingQuantity = newBookingState.quantity;

      // Disabling holes
      teeSheetStore.selectedTeeTime.divisions_available?.forEach(division => {
        // 9 holes available
        if (division === 1) {
          holeButtons[1] = false;
        } else if (division === 2) {
          // Only 9 holes available checks if turn is a league or tournament, unless the turn is also in the same league/tournament
          if (
            (teeSheetStore.selectedTeeTime.divisions_available?.length > 1 &&
              teeSheetStore.selectedTeeTime?.turn_tee_time === null) ||
            isTurnBlockedForEighteenHoles(
              teeSheetStore?.selectedTeeTime?.turn_tee_time?.blocked_type,
              teeSheetStore?.selectedTeeTime?.blocked_type,
            ) ||
            ((teeSheetStore.selectedTeeTime?.turn_tee_time?.league_id ||
              teeSheetStore.selectedTeeTime?.turn_tee_time?.tournament_id) &&
              (teeSheetStore.selectedTeeTime?.league_id !== teeSheetStore.selectedTeeTime?.turn_tee_time?.league_id ||
                teeSheetStore.selectedTeeTime?.tournament_id !==
                  teeSheetStore.selectedTeeTime?.turn_tee_time?.tournament_id))
          ) {
            holeButtons[1] = false;
          } else if (teeSheetStore.selectedTeeTime?.turn_tee_time != null) {
            // 18 holes available
            if (teeSheetStore.selectedTeeTime?.turn_tee_time?.quantity_remaining >= bookingQuantity) {
              holeButtons[0] = false;
            } else {
              // Only 9 holes available
              holeButtons[1] = false;
            }
          }
        }
      });

      if (mounted) {
        if (holeButtons[0] && holeButtons[1]) {
          updateState<INewBookingState>({ holeButtons, playerButtons, holes: null }, StateType.NewBookingState);
        } else if (holeButtons[0]) {
          updateState<INewBookingState>({ holeButtons, playerButtons, holes: 9 }, StateType.NewBookingState);
        } else if (holeButtons[1]) {
          updateState<INewBookingState>({ holeButtons, playerButtons, holes: 18 }, StateType.NewBookingState);
        }
        updateState<INewBookingState>({ holeButtons, playerButtons }, StateType.NewBookingState);
      }
    };

    if (uiStore.newLotteryRequestActive === true) {
      void disableOptions();
    }

    return () => {
      mounted = false;
    };
  }, [
    teeSheetStore.selectedTeeTime,
    newBookingState.holes,
    uiStore.newLotteryRequestActive,
    newBookingState.quantity,
    newBookingState?.forceBooking,
  ]);

  useEffect(() => {
    let initialQuantity = 0;
    let maxQuantity = 0;
    let currentBookings = 0;

    if (props.slot !== null) {
      if (teeSheetStore?.selectedTeeTime?.quantity_remaining === teeSheetStore?.selectedTeeTime?.slots?.length) {
        initialQuantity = props.slot?.tee_time_slot_position;
      } else {
        for (let i = 0; i < teeSheetStore?.selectedTeeTime?.slots?.length; i++) {
          if (teeSheetStore?.selectedTeeTime?.slots[i]?.status === "booked") {
            currentBookings = currentBookings + 1;
          }
        }
        initialQuantity = props.slot?.tee_time_slot_position - currentBookings;
      }
    } else {
      maxQuantity = teeSheetStore?.selectedTeeTime?.quantity_remaining;
    }

    if (teeSheetStore.selectedTeeTime?.cart_rule == "required") {
      updateState<INewBookingState>(
        {
          holes: 18,
          quantity: props.slot !== null ? initialQuantity : maxQuantity,
          timePreference1: teeSheetStore?.selectedTeeTime?.start_time,
          powerCartQuantity: 1,
        },
        StateType.NewBookingState,
      );
      return;
    }

    updateState<INewBookingState>(
      {
        holes: 18,
        quantity: props.slot !== null ? initialQuantity : maxQuantity,
        timePreference1: teeSheetStore?.selectedTeeTime?.start_time,
        powerCartQuantity: 0,
      },
      StateType.NewBookingState,
    );
  }, [uiStore.newLotteryRequestActive, teeSheetStore.selectedTeeTime]);

  useEffect(() => {
    if (
      newBookingState?.forceBooking &&
      props?.lotteryBuilds?.some(build => build.tee_time_id === teeSheetStore?.selectedTeeTime?.id)
    ) {
      const buildQuantity = props?.lotteryBuilds
        ?.filter(build => build?.tee_time_id === teeSheetStore?.selectedTeeTime?.id)
        ?.reduce((total, build) => {
          return total + build?.quantity;
        }, 0);
      const maxQuantity = teeSheetStore?.selectedTeeTime?.quantity_remaining - (buildQuantity ?? 0);

      updateState<INewBookingState>({ quantity: maxQuantity }, StateType.NewBookingState);
      void handlePlayersChange(maxQuantity);
    }
  }, [newBookingState?.forceBooking]);

  const handleCustomerSelection = (id: number | string, slot: ISlot, newCustomer: Record<string, any>) => {
    if (newBookingState.customerSelection.length === teeSheetStore.selectedTeeTime?.slots.length) {
      return;
    }

    if (id === "") {
      return;
    } else {
      const selection = !newCustomer
        ? state["golferSearchResult".concat(slot.position.toString())]
            .filter((res: ICustomer) => {
              if (res.id === id) {
                return true;
              } else {
                return false;
              }
            })
            .map((res: ICustomer) => res)
        : [newCustomer];

      const customerSelection = newBookingState.customerSelection;
      customerSelection.splice(slot.position - 1, 0, selection[0] as ICustomer);

      const teeTimeSlots = newBookingState.teeTimeSlots;
      teeTimeSlots.splice(slot.position - 1, 0, { id: slot.id, customer_id: selection[0].id, guest: false });

      updateState({ customerSelection, teeTimeSlots }, StateType.NewBookingState);
    }
  };

  const closeNewCustomer = () => {
    updateState<INewPlayerState>(
      {
        newCustomer: null,
        selectedSlot: null,
        newCustomerSheetActive: false,
      },
      StateType.NewPlayerState,
    );

    updateState<IMainState>(
      {
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
        golferSearchQuery5: "",
        golferSearchQuery6: "",
      },
      StateType.MainState,
    );
  };

  const addCustomer = async (customerInfo: ICustomerInfoState) => {
    const customer = await createCustomer(customerInfo);
    if (customer) {
      handleCustomerSelection(customer.id, newPlayerState.selectedSlot, customer);
    }
  };

  const createCustomer = async (customerInfo: ICustomerInfoState) => {
    const error =
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "");

    if (error) {
      return null;
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber || null,
        email: customerInfo.emailAddress || null,
        state: "disabled",
        send_welcome_email: customerInfo.sendWelcomeEmail,
      },
      true,
    );

    if (Number(customerRes.status) !== 200) {
      dispatch(showError(customerErrorMessage(t, customerRes?.data?.message as string)));
    }

    if (Number(customerRes.status) === 200) {
      updateState<INewPlayerState>({ newCustomer: customerRes.data.data }, StateType.NewPlayerState);
      void closeNewCustomer();
      // dispatch(showSuccess(customerRes.data.message));
      dispatch(showSuccess(t("Customer created successfull")));
      return customerRes.data as ICustomer;
    }
    return null;
  };

  const removeGolfer = (value: any) => {
    // remove golfer by unique id
    const customerSelection = newBookingState.customerSelection.filter((golfer: any) => {
      return golfer !== value;
    });

    const teeTimeSlots = newBookingState.teeTimeSlots.filter((slot: any) => slot.customer_id !== value.id);
    updateState<INewBookingState>({ customerSelection, teeTimeSlots }, StateType.NewBookingState);
  };

  const handleHolesChange = (holes: number) => {
    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      const numericPowerCartQuantity = newBookingState.powerCartQuantity;

      let updatedPowerCartQuantity: number = null;

      if (holes === 9 && numericPowerCartQuantity > props.cartsAvailable.nineHole) {
        updatedPowerCartQuantity = props.cartsAvailable.nineHole;
      } else if (holes === 18 && numericPowerCartQuantity > props.cartsAvailable.eighteenHole) {
        updatedPowerCartQuantity = props.cartsAvailable.eighteenHole;
      } else {
        updatedPowerCartQuantity = newBookingState.powerCartQuantity;
      }

      updateState<INewBookingState>({ holes, powerCartQuantity: updatedPowerCartQuantity }, StateType.NewBookingState);
    } else {
      updateState<INewBookingState>({ holes }, StateType.NewBookingState);
    }
  };

  const handlePlayersChange = (e: number) => {
    const quantity = e;
    // If the new selected quanitity is less than the previous quantity, remove the extra players
    if (quantity < newBookingState.quantity) {
      void handleRemoverGolfers(quantity);
    }
    updateState<INewBookingState>(
      {
        quantity,
      },
      StateType.NewBookingState,
    );
  };

  const handleRemoverGolfers = (newQuantity: number) => {
    const updatedCustomerSelection = newBookingState.customerSelection.filter((_, index) => index + 1 <= newQuantity);
    const updatedTeeTimeSlots = newBookingState.teeTimeSlots.filter((slot, index) => index + 1 <= newQuantity);
    updateState<INewBookingState>(
      { customerSelection: updatedCustomerSelection, teeTimeSlots: updatedTeeTimeSlots },
      StateType.NewBookingState,
    );
    updateState<IMainState>(
      {
        golferSearchQuery1: "",
        golferSearchQuery2: "",
        golferSearchQuery3: "",
        golferSearchQuery4: "",
        golferSearchQuery5: "",
        golferSearchQuery6: "",
      },
      StateType.MainState,
    );
  };

  const handleClosePopup = () => {
    updateState<INewBookingState>({ teetimeLockPopupVisible: false }, StateType.NewBookingState);
  };

  const toggleNewCustomerSheet = (slot: ISlot) => {
    updateState<INewPlayerState>(
      {
        selectedSlot: slot,
        newCustomerSheetActive: !newPlayerState.newCustomerSheetActive,
      },
      StateType.NewPlayerState,
    );

    openNewCustomerSheet();
  };

  function openNewCustomerSheet() {
    setNewPlayerState(prevState => ({
      ...prevState,
      newCustomerSheetActive: true,
    }));
  }

  function getBookingCartsAvailable() {
    if (!facilityStore.teeSheetSettings?.validate_power_cart_count) {
      return 0;
    } else if (newBookingState.holes === 9) {
      return props.cartsAvailable.nineHole;
    } else if (newBookingState.holes === 18) {
      return props.cartsAvailable.eighteenHole;
    } else {
      return 0;
    }
  }

  const bookingCartsAvailable = getBookingCartsAvailable();

  return (
    <div>
      <NewCustomer
        newCustomerSheetActive={newPlayerState.newCustomerSheetActive}
        onCancel={closeNewCustomer}
        onOk={info => void addCustomer(info)}
        searchValue={newPlayerState.currentSearch}
      />

      <Portal isMounted={uiStore.newLotteryRequestActive}>
        <Sheet
          open={uiStore.newLotteryRequestActive}
          size="medium"
          closable={!newBookingState.requestPosting}
          title={newBookingState?.forceBooking ? t("Lottery Booking") : t("Lottery Request")}
          height="flexible"
          onCancel={closeNewRequest}
          onOk={handleOnOk}
          cancelText={t("Clear")}
          okText={newBookingState?.forceBooking ? t("Save Booking") : t("Submit Request")}
          okDisabled={
            newBookingState.customerSelection.length === 0 ||
            newBookingState.holes === null ||
            newBookingState.quantity === null ||
            newBookingState.requestPosting ||
            !newBookingState?.timePreference1
              ? true
              : false
          }
          cancelDisabled={newBookingState.requestPosting}
          backDropCancel={false}
        >
          {props.loading || newBookingState.requestPosting ? (
            <div className="text-center">
              <div style={{ height: "32px", overflow: "hidden" }}>
                <Spin />
              </div>
              <div className="mt-2">{props.loading ? props.loadingText : t("Creating Booking")}</div>
            </div>
          ) : (
            <>
              <span className="text-primary-grey text-12">
                {moment(`${teeSheetStore.selectedTeeTime?.date}T${teeSheetStore.selectedTeeTime?.start_time}`).format(
                  "LLL",
                )}
              </span>
              <>
                <div className="booking-modal">
                  <div className="booking-details">
                    <div className="booking-options">
                      <div className="booking-options_option">
                        <h3>{t("Holes")}</h3>

                        <Radio.Group name="holes_new" onChange={handleHolesChange} value={newBookingState.holes}>
                          {newBookingState.holeButtons?.map((disabled, i) => {
                            const holes = i === 0 ? 18 : 9;
                            return (
                              <Radio.Button key={`holes_new_${holes}`} value={holes} disabled={disabled}>
                                {holes}
                              </Radio.Button>
                            );
                          })}
                        </Radio.Group>
                      </div>
                      <div className="booking-options_option">
                        <h3>{t("Players")}</h3>
                        <div className="flex flex-row">
                          <Radio.Group
                            name="players_new"
                            onChange={(e: number) => void handlePlayersChange(e)}
                            value={newBookingState.quantity}
                          >
                            {newBookingState.playerButtons?.map((disabled, i) => {
                              const players = i + 1;
                              return (
                                <Radio.Button key={`players_new_${players}`} value={players} disabled={disabled}>
                                  {players}
                                </Radio.Button>
                              );
                            })}
                          </Radio.Group>
                        </div>
                      </div>
                      {newBookingState?.forceBooking ? (
                        <div className="booking-options_option">
                          <h3>{t("Power Carts")}</h3>
                          <Radio.Group
                            name="power_carts_new"
                            onChange={(e: number) => setNewBookingState(prev => ({ ...prev, powerCartQuantity: e }))}
                            value={newBookingState.powerCartQuantity}
                          >
                            <Radio.Button value={0} disabled={teeSheetStore.selectedTeeTime?.cart_rule == "required"}>
                              0
                            </Radio.Button>

                            <Radio.Button
                              value={1}
                              disabled={
                                teeSheetStore.selectedTeeTime?.cart_rule == "none" ||
                                (bookingCartsAvailable < 1 && facilityStore.teeSheetSettings?.validate_power_cart_count)
                              }
                            >
                              1
                            </Radio.Button>

                            <Radio.Button
                              value={2}
                              disabled={
                                teeSheetStore.selectedTeeTime?.cart_rule == "none" ||
                                (bookingCartsAvailable < 2 && facilityStore.teeSheetSettings?.validate_power_cart_count)
                              }
                            >
                              2
                            </Radio.Button>
                          </Radio.Group>
                        </div>
                      ) : null}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridColumnGap: "1rem",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        marginTop: "",
                      }}
                    >
                      {newBookingState?.quantity &&
                        teeSheetStore.selectedTeeTime?.slots
                          .filter(slot => slot.status === "available")
                          .map((slot: ISlot, index: number) => {
                            const golferPosition = slot.position.toString();
                            const queryKey = "golferSearchQuery".concat(golferPosition);
                            const searchResultKey = "golferSearchResult".concat(golferPosition);
                            //Get the matching slot that was added to the selection
                            const slotFound = newBookingState.teeTimeSlots.find(element => element.id === slot.id);
                            if (slotFound) {
                              //Get the golfer info from customer selection
                              const golfer = newBookingState.customerSelection.find(
                                golfer => golfer.id === slotFound.customer_id,
                              );
                              if (golfer != null) {
                                return (
                                  <GolferCard
                                    closable
                                    removeGolfer={() => removeGolfer(golfer)}
                                    email={golfer?.email}
                                    name={golfer?.full_name}
                                    memberCode={golfer?.member_code}
                                    customerType={golfer?.customer_type}
                                    phone={golfer?.phone}
                                    key={index}
                                  />
                                );
                              }
                            } else {
                              return (
                                <div key={index} className="booking-player-dropdown">
                                  <Select
                                    label={t("Player").concat(golferPosition)}
                                    placeholder={t("Golfer")}
                                    showSearch
                                    className={`flex justify-center align-center w-full h-10 position-relative z-20 text-black font-medium appearance-none border-none focus:outline-none placeholder-gray-200`}
                                    onSearch={(query: string) => handleCustomerSearch(query, queryKey)}
                                    onChange={(id: string | number, slot: ISlot) =>
                                      handleCustomerSelection(id, slot, null)
                                    }
                                    allowClear
                                    searchValue={state[queryKey]}
                                    showDropDownOnFocus={true}
                                    searching={state.searching}
                                    disabled={index + 1 <= newBookingState.quantity ? false : true}
                                    usePortal
                                    autoFocus={index + 1 === newBookingState.customerSelection?.length + 1}
                                  >
                                    <Select.Action
                                      className="ui-select-dropdown-list-item"
                                      onClick={() => toggleNewCustomerSheet(slot)}
                                    >
                                      <p className="text-md">{t("New Customer")}</p>
                                    </Select.Action>
                                    {state[searchResultKey].map((golfer: any, i: number) => {
                                      const restrictions = teeSheetStore.selectedTeeTime.active_restrictions?.filter(
                                        restriction => restriction.customer_type_id === golfer?.customer_type_id,
                                      );
                                      //Banned golfer
                                      if (
                                        golfer != null &&
                                        (golfer?.banned ||
                                          restrictions.filter(restriction => restriction.block_bookings).length > 0)
                                      ) {
                                        return (
                                          <div
                                            key={i}
                                            className="flex justify-between bg-gray-100 cursor-not-allowed"
                                            style={{ padding: "8px 16px" }}
                                          >
                                            <div>
                                              <div className="font-semibold text-lg">{golfer?.full_name}</div>
                                              <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                              <div className="text-sm text-gray-500">{golfer.email}</div>
                                              <div className="text-sm text-gray-500">
                                                {golfer.phone ? golfer.phone : null}
                                              </div>
                                            </div>
                                            <div className="flex flex-column justify-between">
                                              <div className="flex flex-column gap-2">
                                                {golfer.has_high_priority_notes ? (
                                                  <div
                                                    className="font-medium text-error self-end gap-2 flex"
                                                    style={{ alignItems: "center" }}
                                                    onClick={e => {
                                                      e.stopPropagation();
                                                      window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                    }}
                                                  >
                                                    <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                    <p>{t("Click to view notes")}</p>
                                                  </div>
                                                ) : (
                                                  <div />
                                                )}
                                                {golfer?.banned ? (
                                                  <div className="ml-auto">
                                                    <Badge size="small" type="warning">
                                                      Banned
                                                    </Badge>
                                                  </div>
                                                ) : null}
                                                {restrictions.length > 0 && !golfer?.banned ? (
                                                  <OverlayTrigger
                                                    placement="top-end"
                                                    overlay={
                                                      <Tooltip id="restricted-booking-tooltip">{`${t(
                                                        "Tee time outside of customer booking hours",
                                                      )}`}</Tooltip>
                                                    }
                                                  >
                                                    <div className="ml-auto">
                                                      <Badge size="small" type="warning">
                                                        <FontAwesomeIcon className="mr-1" icon={faCircleExclamation} />
                                                        Restricted
                                                      </Badge>
                                                    </div>
                                                  </OverlayTrigger>
                                                ) : null}
                                              </div>
                                              <div className="font-medium text-base text-gray-500 self-end">
                                                {golfer.member_code}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }
                                      if (golfer !== null && golfer !== undefined) {
                                        return (
                                          <Option key={i} value={golfer.id} extraValues={slot}>
                                            <div className="flex justify-between">
                                              <div>
                                                <div className="font-semibold text-lg">{golfer?.full_name}</div>
                                                <div className="text-sm text-gray-500">{golfer.customer_type}</div>
                                                <div className="text-sm text-gray-500">{golfer.email}</div>
                                                <div className="text-sm text-gray-500">
                                                  {golfer.phone ? golfer.phone : null}
                                                </div>
                                              </div>
                                              <div className="flex flex-column justify-between">
                                                {golfer.has_high_priority_notes ? (
                                                  <div
                                                    className="font-medium text-error self-end gap-2 flex"
                                                    style={{ alignItems: "center" }}
                                                    onClick={e => {
                                                      e.stopPropagation();
                                                      window.open(`/admin/customers/${String(golfer.id)}/notes`);
                                                    }}
                                                  >
                                                    <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                                    <p>{t("Click to view notes")}</p>
                                                  </div>
                                                ) : (
                                                  <div />
                                                )}
                                                {restrictions.length > 0 ? (
                                                  <OverlayTrigger
                                                    placement="top-end"
                                                    overlay={
                                                      <Tooltip id="restricted-booking-tooltip">{`${t(
                                                        "Tee time outside of customer booking hours",
                                                      )}`}</Tooltip>
                                                    }
                                                  >
                                                    <div className="ml-auto">
                                                      <Badge size="small" type="warning">
                                                        <FontAwesomeIcon className="mr-1" icon={faCircleExclamation} />
                                                        Restricted
                                                      </Badge>
                                                    </div>
                                                  </OverlayTrigger>
                                                ) : null}
                                                <div className="font-medium text-base text-gray-500 self-end">
                                                  {golfer.member_code}
                                                </div>
                                              </div>
                                            </div>
                                          </Option>
                                        );
                                      }
                                    })}
                                  </Select>
                                </div>
                              );
                            }
                          })}
                    </div>

                    <br />
                    <FormLayout>
                      {!newBookingState?.forceBooking ? (
                        <FormLayout.Group>
                          <Select
                            label={t(`Time Preference 1`)}
                            defaultValue={newBookingState?.timePreference1}
                            onChange={id =>
                              updateState<INewBookingState>({ timePreference1: id }, StateType.NewBookingState)
                            }
                            usePortal
                          >
                            {props?.lotteryTeeTimes
                              ?.filter(
                                time =>
                                  time?.start_time !== newBookingState?.timePreference2 &&
                                  time?.start_time !== newBookingState?.timePreference3,
                              )
                              ?.map((time, index) => {
                                return (
                                  <Option key={index} value={time?.start_time}>
                                    {moment(time?.start_time, "HH:mm")?.format("LT")}
                                  </Option>
                                );
                              })}
                          </Select>
                          {props?.lottery?.number_of_time_preferences > 1 &&
                          !props?.lottery?.allow_time_preference_window ? (
                            <Select
                              label={t(`Time Preference 2`)}
                              defaultValue={newBookingState?.timePreference2}
                              onChange={id =>
                                updateState<INewBookingState>({ timePreference2: id }, StateType.NewBookingState)
                              }
                              usePortal
                            >
                              {props?.lotteryTeeTimes
                                ?.filter(
                                  time =>
                                    time?.start_time !== newBookingState?.timePreference1 &&
                                    time?.start_time !== newBookingState?.timePreference3,
                                )
                                ?.map((time, index) => {
                                  return (
                                    <Option key={index} value={time?.start_time}>
                                      {moment(time?.start_time, "HH:mm")?.format("LT")}
                                    </Option>
                                  );
                                })}
                            </Select>
                          ) : null}
                          {props?.lottery?.number_of_time_preferences > 2 &&
                          !props?.lottery?.allow_time_preference_window ? (
                            <Select
                              label={t(`Time Preference 3`)}
                              defaultValue={newBookingState?.timePreference3}
                              onChange={id =>
                                updateState<INewBookingState>({ timePreference3: id }, StateType.NewBookingState)
                              }
                              usePortal
                            >
                              {props?.lotteryTeeTimes
                                ?.filter(
                                  time =>
                                    time?.start_time !== newBookingState?.timePreference1 &&
                                    time?.start_time !== newBookingState?.timePreference2,
                                )
                                ?.map((time, index) => {
                                  return (
                                    <Option key={index} value={time?.start_time}>
                                      {moment(time?.start_time, "HH:mm")?.format("LT")}
                                    </Option>
                                  );
                                })}
                            </Select>
                          ) : null}

                          {props?.lottery?.allow_time_preference_window ? (
                            <Select
                              label={t("Start Time Preference Window")}
                              defaultValue={newBookingState?.startTimePreferenceWindow}
                              onChange={id =>
                                updateState<INewBookingState>(
                                  { startTimePreferenceWindow: id },
                                  StateType.NewBookingState,
                                )
                              }
                              usePortal
                            >
                              {[
                                { id: 1, label: t("1 Hour") },
                                { id: 2, label: t("2 Hours") },
                                { id: 3, label: t("3 Hours") },
                                { id: 4, label: t("4 Hours") },
                                { id: 5, label: t("5 Hours") },
                                { id: 6, label: t("6 Hours") },
                                { id: 7, label: t("7 Hours") },
                                { id: 8, label: t("8 Hours") },
                              ]?.map((window, index) => {
                                return (
                                  <Option key={index} value={window.id}>
                                    {window.label}
                                  </Option>
                                );
                              })}
                            </Select>
                          ) : null}
                        </FormLayout.Group>
                      ) : null}
                    </FormLayout>
                    <div className="flex items-center justify-between">
                      <Toggle
                        label={t("Force Booking")}
                        tooltip={t("Create a booking for the selected tee time within this lottery group")}
                        onChange={e => setNewBookingState(prev => ({ ...prev, forceBooking: e.target.checked }))}
                        checked={newBookingState?.forceBooking}
                        size="medium"
                      />
                      {newBookingState?.forceBooking ? (
                        <div className="ml-auto">
                          <Checkbox
                            size="small"
                            checked={newBookingState?.sendBookingConfirmation}
                            onChange={e =>
                              setNewBookingState(prev => ({ ...prev, sendBookingConfirmation: e.target.checked }))
                            }
                            label={t("Send Booking Confirmation")}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            </>
          )}
        </Sheet>
      </Portal>

      <Popup
        open={newBookingState.teetimeLockPopupVisible}
        type="warning"
        title={t("Tee Time Locked!")}
        description={t("Tee time is currently being booked by another user")}
        onOk={() => handleClosePopup()}
        okText={t("Go Back")}
        onCancel={() => handleClosePopup()}
        backDropCancel={true}
      />
    </div>
  );
}
